import React, { useEffect, useMemo, useRef, useState } from "react";

function getOptionNodes(children) {
  return React.Children.toArray(children).filter(
    (child) => React.isValidElement(child) && child.type === "option"
  );
}

function ThemeSelect({
  value,
  defaultValue,
  onChange,
  children,
  className = "",
  style,
  disabled = false,
  name,
  id,
  "aria-label": ariaLabel,
  ...rest
}) {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(
    defaultValue !== undefined ? String(defaultValue) : ""
  );
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const currentValue = isControlled ? String(value ?? "") : internalValue;

  const options = useMemo(() => {
    return getOptionNodes(children).map((child, index) => {
      const optionValue =
        child.props.value !== undefined
          ? String(child.props.value)
          : String(child.props.children ?? "");
      return {
        key: child.key ?? `${optionValue}-${index}`,
        value: optionValue,
        label: child.props.children,
        disabled: Boolean(child.props.disabled),
        hidden: Boolean(child.props.hidden),
      };
    });
  }, [children]);

  const visibleOptions = useMemo(
    () => options.filter((option) => !option.hidden),
    [options]
  );

  const selectedOption =
    options.find((option) => option.value === currentValue) ||
    visibleOptions[0] ||
    null;

  const emitChange = (nextValue) => {
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    if (typeof onChange === "function") {
      onChange({
        target: { value: nextValue, name: name || "" },
        currentTarget: { value: nextValue, name: name || "" },
      });
    }
  };

  const closeMenu = () => {
    setOpen(false);
    setHighlightIndex(-1);
  };

  const openMenu = () => {
    if (disabled) return;
    const selectedIndex = visibleOptions.findIndex(
      (option) => option.value === currentValue
    );
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const selectOption = (option) => {
    if (!option || option.disabled) return;
    emitChange(option.value);
    closeMenu();
  };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightIndex((prev) => {
          if (!visibleOptions.length) return -1;
          const direction = event.key === "ArrowDown" ? 1 : -1;
          let next = prev;
          for (let i = 0; i < visibleOptions.length; i += 1) {
            next =
              (next + direction + visibleOptions.length) %
              visibleOptions.length;
            if (!visibleOptions[next].disabled) return next;
          }
          return prev;
        });
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (highlightIndex >= 0) {
          selectOption(visibleOptions[highlightIndex]);
        }
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, highlightIndex, visibleOptions]);

  useEffect(() => {
    if (!open || highlightIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[highlightIndex];
    if (item && typeof item.scrollIntoView === "function") {
      item.scrollIntoView({ block: "nearest" });
    }
  }, [open, highlightIndex]);

  const isSmall = className.includes("form-select-sm");

  return (
    <div
      ref={rootRef}
      className={`theme-select ${isSmall ? "theme-select--sm" : ""} ${
        open ? "theme-select--open" : ""
      } ${disabled ? "theme-select--disabled" : ""} ${className}`}
      style={style}
      {...rest}
    >
      <button
        type="button"
        id={id}
        name={name}
        className="theme-select__trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? closeMenu() : openMenu())}
      >
        <span className="theme-select__value">
          {selectedOption?.label ?? ""}
        </span>
        <span className="theme-select__chevron" aria-hidden="true" />
      </button>

      {open && (
        <ul
          ref={listRef}
          className="theme-select__menu"
          role="listbox"
          aria-activedescendant={
            highlightIndex >= 0
              ? `theme-select-option-${highlightIndex}`
              : undefined
          }
        >
          {visibleOptions.map((option, index) => {
            const isSelected = option.value === currentValue;
            const isHighlighted = index === highlightIndex;
            return (
              <li
                key={option.key}
                id={`theme-select-option-${index}`}
                role="option"
                aria-selected={isSelected}
                className={`theme-select__option ${
                  isSelected ? "is-selected" : ""
                } ${isHighlighted ? "is-highlighted" : ""} ${
                  option.disabled ? "is-disabled" : ""
                }`}
                onMouseEnter={() => setHighlightIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectOption(option);
                }}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ThemeSelect;
